/**
 * Cross-tab communication composable
 *
 * Use cases:
 * - Newly opened tabs will request state data from the origin tab. Communication channel will be
 * closed once the transaction is completed.
 *
 */
import { isRef, onBeforeMount } from "vue";
import { v4 as uuid } from "uuid";
var MessageType;
(function (MessageType) {
    MessageType["REQUEST"] = "REQUEST";
    MessageType["RESPONSE"] = "RESPONSE";
})(MessageType || (MessageType = {}));
// Global channel node identifier (ensuring there is only one per tab)
const currentNodeId = uuid();
// Composable
export const useCrossTabCom = (channelName, refs, options) => {
    // The local storage key used to inform new tabs that an emitter tab is ready for communication
    const localStorageItemKey = `${channelName}-origin-node-id`;
    const requestMsgHandler = (channel, event) => {
        const { type, data, to, from } = JSON.parse(event.data ?? "{}");
        // Only process RESPONSE messages with data sent from other channel nodes intended for current tab
        if (type !== MessageType.RESPONSE || from === currentNodeId || to !== currentNodeId)
            return;
        // Update refs with the received data.
        Object.entries(data ?? {}).forEach(([key, value]) => {
            if (isRef(refs[key])) {
                const keysToPreserve = options?.preserveKeys?.[key];
                if (keysToPreserve && typeof value === "object" && value !== null) {
                    // Preserve specified local keys while merging the rest from broadcast
                    const preserved = keysToPreserve.reduce((acc, preserveKey) => {
                        if (preserveKey in refs[key].value) {
                            acc[preserveKey] = refs[key].value[preserveKey];
                        }
                        return acc;
                    }, {});
                    refs[key].value = { ...value, ...preserved };
                }
                else {
                    refs[key].value = value;
                }
            }
        });
        // Terminate the tab's BroadcastChannel instance since it's no longer needed.
        channel.close();
    };
    const responseMsgHandler = (channel, event) => {
        const { type, from, to } = JSON.parse(event.data ?? "{}");
        // Only process REQUEST messages sent from other channel nodes intended for current tab
        if (type !== MessageType.REQUEST || currentNodeId === from || to !== currentNodeId)
            return;
        // Send the refs values to the requesting tab and close the channel.
        const payload = {
            type: MessageType.RESPONSE,
            from: currentNodeId,
            to: from,
            data: Object.entries(refs).reduce((acc, [key, ref]) => ({ ...acc, [key]: ref.value }), {})
        };
        channel.postMessage(JSON.stringify(payload));
        channel.close();
    };
    // This ensures that new tabs request data when opened
    onBeforeMount(() => {
        // Verify if an emitter tab is ready for communication to avoid unnecessary BroadcastChannel initialization.
        const originNodeId = localStorage.getItem(localStorageItemKey);
        if (!originNodeId)
            return;
        // Remove the local storage item to prevent interference with other tabs.
        localStorage.removeItem(localStorageItemKey);
        // Listen for incoming RESPONSE messages from other tabs
        const channel = new BroadcastChannel(channelName);
        channel.onmessage = (event) => requestMsgHandler(channel, event);
        // Send a request message to the emitter tab
        const payload = {
            type: MessageType.REQUEST,
            to: originNodeId,
            from: currentNodeId
        };
        channel.postMessage(JSON.stringify(payload));
        // Close the channel after 1 second to prevent memory leak.
        setTimeout(() => channel.close(), 1000);
    });
    // A function to be called from the origin tab to set the communication channel
    const initBroadcast = () => {
        // Save the emitter node's identifier in the local storage to inform new tabs that an emitter tab is ready
        localStorage.setItem(localStorageItemKey, currentNodeId);
        // Listen for incoming 'REQUEST' messages from other tabs
        const channel = new BroadcastChannel(channelName);
        // Set listeners for incoming messages
        channel.onmessage = (event) => responseMsgHandler(channel, event);
    };
    return {
        initBroadcast
    };
};
